import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { Notification } from '../types';
import { clsx } from 'clsx';

interface NotificationPortalProps {
    notifications: Notification[];
    removeNotification: (id: string) => void;
}

const NotificationPortal: React.FC<NotificationPortalProps> = ({ notifications, removeNotification }) => {
    return createPortal(
        <div className="fixed bottom-24 right-6 z-[9999] flex flex-col gap-3 pointer-events-none max-w-md w-full px-4 sm:px-0" aria-live="polite" aria-relevant="additions">
            {notifications.map((notification) => (
                <NotificationPill
                    key={notification.id}
                    notification={notification}
                    onClose={() => removeNotification(notification.id)}
                />
            ))}
        </div>,
        document.body
    );
};

interface NotificationPillProps {
    notification: Notification;
    onClose: () => void;
}

const NotificationPill: React.FC<NotificationPillProps> = ({ notification, onClose }) => {
    const { type, message, description, duration = 5000, dismissible = true } = notification;

    useEffect(() => {
        if (dismissible && duration > 0) {
            const timer = setTimeout(() => {
                onClose();
            }, duration);
            return () => clearTimeout(timer);
        }
    }, [dismissible, duration, onClose]);

    const icons = {
        error: <AlertCircle className="w-5 h-5 text-rose-500" />,
        success: <CheckCircle className="w-5 h-5 text-emerald-500" />,
        info: <Info className="w-5 h-5 text-blue-500" />,
        warning: <AlertTriangle className="w-5 h-5 text-amber-500" />,
    };

    const bgClasses = {
        error: "bg-rose-50/95 dark:bg-rose-950/90 border-rose-200 dark:border-rose-800/50 text-rose-900 dark:text-rose-100",
        success: "bg-emerald-50/95 dark:bg-emerald-950/90 border-emerald-200 dark:border-emerald-800/50 text-emerald-900 dark:text-emerald-100",
        info: "bg-blue-50/95 dark:bg-blue-950/90 border-blue-200 dark:border-blue-800/50 text-blue-900 dark:text-blue-100",
        warning: "bg-amber-50/95 dark:bg-amber-950/90 border-amber-200 dark:border-amber-800/50 text-amber-900 dark:text-amber-100",
    };

    return (
        <div
            className={clsx(
                "pointer-events-auto flex items-start gap-3 p-4 rounded-xl border shadow-lg backdrop-blur-md animate-in slide-in-from-right-10 fade-in duration-300",
                bgClasses[type]
            )}
            role="alert"
        >
            <div className="shrink-0 mt-0.5">{icons[type]}</div>
            <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold leading-tight">{message}</h4>
                {description && <p className="mt-1 text-xs opacity-80 leading-relaxed">{description}</p>}
                {notification.action && (
                  <button
                    type="button"
                    onClick={() => { notification.action?.onClick(); onClose(); }}
                    className="mt-1.5 text-xs font-semibold underline underline-offset-2 hover:opacity-80"
                  >
                    {notification.action.label}
                  </button>
                )}
            </div>
            {dismissible && (
                <button
                    onClick={onClose}
                    className="shrink-0 p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                    aria-label="Close notification"
                >
                    <X className="w-4 h-4 opacity-50 hover:opacity-100" />
                </button>
            )}
        </div>
    );
};

export default NotificationPortal;

